// Pure normalization of a fetched Directus `articles` record for the article editor page.
// Extracted from Article.js fetchArticle(); behavior pinned by articlePayload.test.js.

/**
 * Thumbnail state derived from article_thumbnail_url (legacy string or JSON array).
 * Returns only the keys the page must update; `thumbnailGenerationStartTime` is present
 * only when the "generating" timer has to be started.
 */
export const resolveThumbnailState = (data, { thumbnailGenerationStartTime = null } = {}) => {
  const state = {};
  if (data.article_thumbnail_url) {
    if (typeof data.article_thumbnail_url === 'string') {
      // Old format - single URL string
      state.thumbnailData = { url: data.article_thumbnail_url };
      state.thumbnailArray = [{ url: data.article_thumbnail_url, chosen: true }];
      state.currentThumbnailIndex = 0;
      state.isThumbnailGenerating = false;
      console.log('🖼️ THUMBNAIL: Found single thumbnail URL (old format):', data.article_thumbnail_url);
    } else if (Array.isArray(data.article_thumbnail_url)) {
      // New format - array of thumbnails
      state.thumbnailArray = data.article_thumbnail_url;
      const chosenIndex = data.article_thumbnail_url.findIndex(thumb => thumb.chosen);
      const activeIndex = chosenIndex >= 0 ? chosenIndex : 0;
      state.currentThumbnailIndex = activeIndex;
      state.thumbnailData = { url: data.article_thumbnail_url[activeIndex].url };
      state.isThumbnailGenerating = false;
      console.log('🖼️ THUMBNAIL: Found thumbnail array:', data.article_thumbnail_url.length, 'thumbnails, active:', activeIndex);
    }
  } else {
    state.thumbnailData = null;
    state.thumbnailArray = [];
    state.currentThumbnailIndex = 0;

    // Check if thumbnail is currently generating
    // Thumbnail generates if: article is completed/ongoing AND has thumbnail enabled AND less than 5 minutes since update
    const articleCompleted = data.status === 'completed' || data.status === 'success' || data.status === 'ongoing';
    const thumbnailEnabled = data.article_thumbnail_included !== false;
    const minutesSinceUpdate = data.date_updated
      ? (new Date() - new Date(data.date_updated)) / 1000 / 60
      : 999;
    const withinTimeout = minutesSinceUpdate < 5;

    const shouldShowGenerating = articleCompleted && thumbnailEnabled && withinTimeout;
    state.isThumbnailGenerating = shouldShowGenerating;

    if (shouldShowGenerating && !thumbnailGenerationStartTime) {
      state.thumbnailGenerationStartTime = new Date(data.date_updated);
    }

    console.log('🖼️ THUMBNAIL: No thumbnail', {
      status: data.status,
      thumbnailEnabled,
      minutesSinceUpdate: minutesSinceUpdate.toFixed(1),
      showGenerating: shouldShowGenerating
    });
  }
  return state;
};

/** article_quality_check -> array of reports (single report objects are wrapped). */
export const formatQualityCheck = (qualityCheck) => (
  Array.isArray(qualityCheck)
    ? qualityCheck
    : [{ quality_control_report: qualityCheck }]
);

/** `faq` field (array or JSON string) -> FAQ items; invalid data yields []. */
export const parseFaqItems = (data) => {
  let parsedFaq = [];
  if (data.faq) {
    try {
      // If it's already an array, use it directly
      if (Array.isArray(data.faq)) {
        parsedFaq = data.faq;
      } else if (typeof data.faq === 'string') {
        // If it's a string, parse it as JSON
        parsedFaq = JSON.parse(data.faq);
      }
      console.log('Parsed FAQ items:', parsedFaq);
    } catch (error) {
      console.error('Error parsing FAQ data:', error);
      console.log('Raw FAQ data:', data.faq);
      parsedFaq = [];
    }
  }
  return parsedFaq;
};

/** True when linked_datasets is a plain list of ids (not an expanded M2M relation). */
export const isLinkedDatasetIdList = (linkedDatasets) => {
  const firstItem = linkedDatasets[0];
  return typeof firstItem === 'number' || typeof firstItem === 'string';
};

/** Expanded M2M linked_datasets -> dataset objects. */
export const extractRelationDatasets = (linkedDatasets) => linkedDatasets
  .map(relation => {
    console.log('Processing relation:', relation);
    return relation?.datasets_id;
  })
  .filter(dataset => dataset && typeof dataset === 'object');

export const stripThumbnailCacheParam = (url) => {
  if (typeof url !== 'string') return url;
  return url.split('?')[0];
};

/**
 * Thumbnail list after an upload: previous entries lose `chosen` and cache params,
 * the uploaded file becomes the chosen one.
 */
export const buildUploadedThumbnailList = (thumbnailArray, { fileId, fileUrl, createdAt }) => {
  const existingThumbnails = Array.isArray(thumbnailArray)
    ? thumbnailArray.filter((thumb) => thumb?.url)
    : [];
  const normalizedExistingThumbnails = existingThumbnails.map((thumb) => ({
    ...thumb,
    url: stripThumbnailCacheParam(thumb.url),
    chosen: false,
  }));
  const uploadedThumbnail = {
    id: fileId,
    fileId,
    url: fileUrl,
    chosen: true,
    source: 'upload',
    photographer: 'Uploaded',
    created_at: createdAt,
  };
  return [...normalizedExistingThumbnails, uploadedThumbnail];
};
